import {
  donationStatusSchema,
  PRINTOUT_LIST_REASON_MAX_LENGTH,
  PRINTOUT_LIST_REASON_MIN_LENGTH,
  PRINTOUT_LIST_RELEASE_KINDS,
  PRINTOUT_LIST_SOURCES,
  PRINTOUT_LIST_STATUSES,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { listRequestSchema } from './common/list.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import {
  GENERATED_FILE_FAILURE_CODES,
  GENERATED_FILE_STATUSES,
  generatedFileSchema,
} from './generated-file.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const timestampSchema = z.iso.datetime({ offset: true });

/**
 * A printout names at most this many donations. The cap is in the contract, so
 * an unbounded selection is a validation error rather than a document nobody
 * can open — the shape every bulk endpoint here follows.
 */
export const MAX_PRINTOUT_DONATIONS = 500;

/**
 * The most donations the printout of a period's order may hold. A PDF of this
 * many pages is already a print run; a longer order is printed by selection.
 * Past it the request is refused with `GENERATED_FILE_TOO_MANY_ROWS` and the
 * count it found, rather than printing part of the order.
 */
export const MAX_QUEUE_PRINTOUT_DONATIONS = 10_000;

/** A list's own title, saved instead of the default *Liste N* (docs/decisions/0115). */
export const printoutListNameSchema = z.string().trim().min(1).max(120);

/**
 * Two ways to name what is printed, one kind of file: a selection of
 * donations, or **the donations of a period's order not yet on a PDF list** —
 * §4.2's printout of the order. Either way the request makes a PDF list
 * of what it names, in the same transaction as the file's own row, and the
 * worker draws the list (docs/decisions/0092, which amends 0071's reading of
 * the order when the job runs).
 */
export const donationPrintoutInputSchema = z.union([
  z.object({
    donationIds: z
      .array(z.uuid())
      .min(1)
      .max(MAX_PRINTOUT_DONATIONS)
      .refine((ids) => new Set(ids).size === ids.length, 'a donation appears once in one request'),
    name: printoutListNameSchema.optional(),
  }),
  z.object({ campaignPeriodId: z.uuid(), name: printoutListNameSchema.optional() }),
]);

export type DonationPrintoutInput = z.infer<typeof donationPrintoutInputSchema>;

/**
 * What a `donation_printout` file's row holds: since docs/decisions/0092 the
 * list it draws, and — for a file asked for before the lists existed — the
 * request as the route took it, which the worker still draws the old way.
 */
export const donationPrintoutFileRequestSchema = z.union([
  z.object({ printoutListId: z.uuid() }),
  ...donationPrintoutInputSchema.options,
]);

export type DonationPrintoutFileRequest = z.infer<typeof donationPrintoutFileRequestSchema>;

export const printoutListStatusSchema = z.enum(PRINTOUT_LIST_STATUSES);
export const printoutListSourceSchema = z.enum(PRINTOUT_LIST_SOURCES);
export const printoutListReleaseKindSchema = z.enum(PRINTOUT_LIST_RELEASE_KINDS);

/** A list as a donation's row names it: the operations page's *PDF Listesi* column. */
export const printoutListRefSchema = z.object({
  id: z.uuid(),
  number: z.number().int().positive(),
});

export type PrintoutListRef = z.infer<typeof printoutListRefSchema>;

/** The answer to a print: the file being drawn, and the list it was taken in. */
export const printoutRequestResultSchema = generatedFileSchema.extend({
  printoutList: printoutListRefSchema,
});

export type PrintoutRequestResult = z.infer<typeof printoutRequestResultSchema>;

/**
 * One PDF list as the PDF management screen shows it. `file` is its latest drawing;
 * `mine` says whether the reader asked for it, because only the requester may
 * download a generated file (docs/decisions/0043 §7) — anybody else who reads
 * the list draws it again.
 */
export const printoutListSchema = z.object({
  id: z.uuid(),
  number: z.number().int().positive(),
  /** The list's own title, or null for the default *Liste N*. */
  name: z.string().nullable(),
  campaignId: z.uuid(),
  campaignName: z.string(),
  campaignPeriodId: z.uuid().nullable(),
  seasonYear: z.number().int().nullable(),
  source: printoutListSourceSchema,
  status: printoutListStatusSchema,
  /** How many donations the list took when it was made. */
  donationCount: z.number().int().nonnegative(),
  /** How many are on it still: neither removed nor released by a void. */
  activeCount: z.number().int().nonnegative(),
  /** The one place its work is carried out at, where every donation on it agrees. */
  workInstitutionId: z.uuid().nullable(),
  workInstitutionName: z.string().nullable(),
  templateName: z.string().nullable(),
  createdByUserId: z.uuid(),
  createdByName: z.string(),
  createdAt: z.string(),
  voidedAt: z.string().nullable(),
  voidedByName: z.string().nullable(),
  voidReason: z.string().nullable(),
  file: z.object({
    id: z.uuid(),
    status: z.enum(GENERATED_FILE_STATUSES),
    fileName: z.string().nullable(),
    rowCount: z.number().int().nullable(),
    completedAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
    failureCode: z.enum(GENERATED_FILE_FAILURE_CODES).nullable(),
    mine: z.boolean(),
  }),
});

export type PrintoutList = z.infer<typeof printoutListSchema>;

/**
 * The PDF management screen's list: the lists the caller's reach for `donation.print`
 * holds, newest first. Every filter narrows; `search` is a list's number.
 */
export const printoutListListInputSchema = listRequestSchema.extend({
  campaignId: z.uuid().optional(),
  status: printoutListStatusSchema.optional(),
  createdByUserId: z.uuid().optional(),
  createdFrom: timestampSchema.optional(),
  createdTo: timestampSchema.optional(),
});

export type PrintoutListListInput = z.infer<typeof printoutListListInputSchema>;

/** The people who made a list the caller reads, for the creator filter. */
export const printoutListCreatorSchema = z.object({ id: z.uuid(), name: z.string() });

/** The campaigns the caller reads a list of, for the campaign filter. */
export const printoutListCampaignSchema = z.object({ id: z.uuid(), name: z.string() });

export const printoutListPageSchema = paginated(printoutListSchema).extend({
  creators: z.array(printoutListCreatorSchema),
  campaigns: z.array(printoutListCampaignSchema),
});

/** One donation on a list, and — once it left the list — who took it off, when and why. */
export const printoutListMemberSchema = z.object({
  donationId: z.uuid(),
  referenceCode: z.string(),
  position: z.number().int().positive(),
  status: donationStatusSchema,
  donorName: z.string().nullable(),
  donorIsAnonymous: z.boolean(),
  onBehalfOfName: z.string().nullable(),
  workInstitutionName: z.string(),
  printedAt: z.string().nullable(),
  released: z
    .object({
      at: z.string(),
      byName: z.string(),
      kind: printoutListReleaseKindSchema,
      reason: z.string(),
    })
    .nullable(),
});

export type PrintoutListMember = z.infer<typeof printoutListMemberSchema>;

export const printoutListMembersInputSchema = paginationInputSchema.extend({
  id: z.uuid(),
});

/** Required, ten characters at least once trimmed, as a deletion's is — docs/decisions/0085. */
const printoutListReasonSchema = z
  .string()
  .trim()
  .min(PRINTOUT_LIST_REASON_MIN_LENGTH)
  .max(PRINTOUT_LIST_REASON_MAX_LENGTH);

/**
 * A yurt a list's work may be moved to: an active assignment of every season
 * the list's donations are in, shown on forms or not, under the name the
 * campaign shows for it.
 */
export const printoutListWorkInstitutionSchema = z.object({
  institutionId: z.uuid(),
  displayName: z.string(),
  current: z.boolean(),
});

export type PrintoutListWorkInstitution = z.infer<typeof printoutListWorkInstitutionSchema>;

/**
 * PDF management — docs/decisions/0092. Nested under `donation` beside the
 * print route: reading and redrawing a list is `donation.print`, which a campaign
 * staff assignment derives, through the same reach; voiding a list or taking a
 * donation off it is `donation.voidPrintout`, a role's alone.
 */
export const donationPrintoutListContract = {
  printoutLists: oc
    .route({ method: 'GET', path: '/operations/printout-lists' })
    .errors(guarded)
    .input(printoutListListInputSchema)
    .output(printoutListPageSchema),

  printoutList: oc
    .route({ method: 'GET', path: '/operations/printout-lists/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(printoutListSchema),

  printoutListDonations: oc
    .route({ method: 'GET', path: '/operations/printout-lists/{id}/donations' })
    .errors(guarded)
    .input(printoutListMembersInputSchema)
    .output(paginated(printoutListMemberSchema)),

  /**
   * The list drawn again, as a new file asked for by the caller: its donations
   * still on it, in their order, recorded as a print. What a reader who did
   * not ask for the last drawing, or whose drawing's retention ran out, does
   * instead of downloading it. A voided list is not drawn again.
   */
  printoutListRedraw: oc
    .route({ method: 'POST', path: '/operations/printout-lists/{id}/drawings' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(printoutRequestResultSchema),

  /**
   * The whole list voided with a reason: every donation still on it leaves it
   * and may be printed onto another. The paper is not recalled, so a donation
   * printed stays printed (docs/decisions/0092).
   */
  printoutListVoid: oc
    .route({ method: 'POST', path: '/operations/printout-lists/{id}/void' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: printoutListReasonSchema }))
    .output(printoutListSchema),

  /**
   * The list's title and its executing institution (docs/decisions/0115).
   * `name: null` brings back the default *Liste N*. A new `workInstitutionId`
   * moves the planned yurt of every donation still on the list, all or none:
   * one carried out, cancelled or partly cut refuses the whole change.
   */
  printoutListUpdate: oc
    .route({ method: 'PATCH', path: '/operations/printout-lists/{id}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        name: printoutListNameSchema.nullable().optional(),
        workInstitutionId: z.uuid().optional(),
      }),
    )
    .output(printoutListSchema),

  /** The yurts `printoutListUpdate` may name for this list. */
  printoutListWorkInstitutions: oc
    .route({ method: 'GET', path: '/operations/printout-lists/{id}/work-institutions' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ items: z.array(printoutListWorkInstitutionSchema) })),

  /** One donation taken off an active list, with a reason. */
  printoutListRemoveDonation: oc
    .route({ method: 'POST', path: '/operations/printout-lists/{id}/removals' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), donationId: z.uuid(), reason: printoutListReasonSchema }))
    .output(printoutListSchema),
};
